import React, { forwardRef, useId } from 'react';
import clsx from 'clsx';
import { Input as BaseInput } from '@/components/ui/input';
import IconAlert from '@/assets/icons/icon-alert.svg?react';

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'step' | 'min' | 'max' | 'disabled'
> {
  className?: string;
  containerClassName?: string;
  label?: string;
  state?: 'default' | 'disabled' | 'error';
  errorMessage?: string;
  disabled?: boolean; // direct disabled override
  horizontal?: boolean; // layout
  'data-qa-selector'?: string;
}

const baseInputWrapperStyle =
  'h-8 p-2 inline-flex items-center gap-2.5 rounded-md bg-neutral-50 focus-within:outline focus-within:outline-1 focus-within:outline-offset-[-1px] focus-within:outline-indigo-500 transition-colors';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    className = '',
    containerClassName = '',
    label,
    state = 'default',
    errorMessage,
    id,
    name,
    disabled,
    horizontal = false,
    'data-qa-selector': dataQaSelector,
    ...rest
  } = props as InputProps & { step?: never; min?: never; max?: never };

  const autoId = useId();
  const inputId = id || autoId;
  const isDisabled = disabled || state === 'disabled';
  const hasError = state === 'error';
  const effectiveErrorMsg = hasError ? errorMessage || 'Invalid value' : undefined;
  return (
    <>
      <div className={clsx(containerClassName, 'flex w-full flex-col gap-1')}>
        <div
          className={clsx(horizontal ? 'flex flex-row items-center gap-8' : 'flex flex-col gap-2')}
        >
          <div
            className={clsx('flex flex-1 gap-2', {
              'flex-col': !horizontal,
              'flex-row items-center': horizontal,
            })}
          >
            {label && (
              <label
                htmlFor={inputId}
                className={clsx('shrink-0 text-xs leading-none font-medium text-neutral-500')}
              >
                {label}
              </label>
            )}

            <div
              className={clsx(
                baseInputWrapperStyle,
                'relative text-neutral-700',
                hasError && 'outline-red-400 focus-within:outline-red-500',
                isDisabled && 'cursor-not-allowed text-neutral-200',
                className,
              )}
            >
              <BaseInput
                id={inputId}
                name={name}
                ref={ref}
                disabled={isDisabled}
                aria-invalid={hasError || undefined}
                data-qa-selector={dataQaSelector}
                className={clsx(
                  'm-0 w-full border-none bg-transparent p-0 outline-none placeholder:text-neutral-400 focus:ring-0',
                  hasError && 'pr-6',
                  isDisabled && 'text-neutral-200 placeholder:text-neutral-300',
                )}
                {...rest}
              />
              {hasError && (
                <IconAlert
                  aria-hidden="true"
                  className="fill-coral-500 pointer-events-none absolute top-2 right-2 h-4 w-4"
                />
              )}
            </div>
          </div>
        </div>
        {hasError && effectiveErrorMsg && (
          <div
            id={`${inputId}-err`}
            style={{ fontSize: 10, lineHeight: '1.1' }}
            className="text-coral-500 inline-flex w-full justify-end self-end"
          >
            <span
              className="text-coral-500"
              style={{ fontSize: 10, fontWeight: 500, lineHeight: '1' }}
            >
              {effectiveErrorMsg}
            </span>
          </div>
        )}
      </div>
    </>
  );
});
